// Retainers, and the signed agreements a retainer holds. retainerStatus is the
// CONTRACT's state, which is why it lives here and not beside jobStatus.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const retainers = {
  // The income layer: an engagement billed as a whole, monthly, with the jobs beneath
  // it holding the work. The topics are never billed separately.
  retainers: {
    title: 'ריטיינרים',
    newRetainer: 'ריטיינר חדש',
    editRetainer: 'עריכת ריטיינר',
    // Deleting a retainer takes every job beneath it. Closing it keeps the history, and
    // the dialog points there, because the two acts are one click apart.
    deleteRetainer: 'מחיקת ריטיינר',
    deleteScope: 'יימחקו הריטיינר וכל העבודות שתחתיו, על החשבוניות, ההוצאות, המשימות והקבצים שלהן.',
    deleteJobsHeading: 'העבודות שיימחקו:',
    deleteJobs: 'עבודות שתחת הריטיינר',
    // The surprise worth spelling out: a brokerage job under a retainer bills its own
    // income, so its invoices are not the retainer's monthly billing and a partner
    // would not expect them to be at stake here.
    deleteBillingJobsWarning: 'כולל עבודות שמחייבות בעצמן — גם החשבוניות שלהן יימחקו.',
    deleteCloseInstead: 'כדי לסיים בלי למחוק את ההיסטוריה — סגירת התקשרות.',
    deleteBalanceNote: 'המאזן בין השותפים יתעדכן מיד.',
    topicBadgeCost: 'עלויות בלבד',
    topicBadgeBilling: 'מחייבת בעצמה',
    retainerTitle: 'שם הריטיינר',
    monthlyAmount: 'סכום חודשי',
    // תקופת ההתקשרות — שני תאריכים. שדה ריק פירושו "פתוח", לא "חסר": ריטיינר בלי
    // תאריך סיום פשוט רץ עד שמסיימים אותו, ואינו נחשב רשומה לא שלמה.
    startDate: 'תחילת ההתקשרות',
    endDate: 'סיום ההתקשרות',
    term: 'תקופת ההתקשרות',
    monthsUnit: 'חודשים',
    termOpenEnded: 'ללא מועד סיום',
    termNoDates: 'לא הוגדרה תקופת התקשרות',
    termEndsIn: (days) => `נותרו ${days} ימים`,
    termEndsToday: 'מסתיים היום',
    termEndingSoon: 'מתקרב לסיום',
    termEnded: 'ההתקשרות הסתיימה',
    termEndedAgo: (days) => `הסתיים לפני ${days} ימים`,
    // A job under a retainer is CALLED A JOB, by its own name. It used to be labelled
    // "נושא", which asked a partner to learn a second word for a thing they already had
    // one for. The key names still say `topic` — that is the server's domain term for
    // `income_source = 'retainer'` (see CLAUDE.md), and it is a real distinction the code
    // depends on. Only the Hebrew changed; the concept did not.
    topics: 'עבודות',
    topicCost: 'עלויות',
    // A job under this retainer that bills its own charge — a cost bought and passed on.
    // Not "הכנסה עצמאית": that is the form's option name, and to the owner the money is a
    // charge to the client, not income.
    // "תיווך" was jargon; what the owner needs is that this job bills on its own.
    ownIncome: 'מחויבת בנפרד מהריטיינר',
    // The topics table's cost cell on such a job: the part its charge is measured against.
    costAgainstCharge: 'עלה לנו',
    addTopic: 'הוספת עבודה',
    // The count column on the retainers list. Every row is a retainer, so "עבודות" here
    // can only mean the ones beneath it.
    jobCount: 'עבודות',

    // סגירת התקשרות — הפעולה שמסירה את הציפייה לחיוב עתידי, ולא נוגעת בכסף שכבר נרשם.
    // שני מצבי סיום, שנבדלים רק במה שהם *אומרים*: "הסתיים" להתקשרות שמיצתה את עצמה,
    // "בוטל" להתקשרות שנקטעה. כל חישוב שקורא אותם מתייחס לשניהם באותה צורה.
    closeRetainer: 'סגירת התקשרות',
    closeRetainerAction: 'סגירת התקשרות',
    reopenRetainer: 'החזרה לפעיל',
    closeChoice: 'מה קרה להתקשרות?',
    closeCompleted: 'הסתיימה — ההתקשרות מיצתה את עצמה',
    closeCancelled: 'בוטלה — ההתקשרות נקטעה',
    // מה שהגיע מעבודות שמתחת לריטיינר ומחויבות בנפרד — לא רווח של הריטיינר.
    // The section ends in an equation: the engagement's own net profit + the jobs' = the
    // total. Beside the jobs, the costs the engagement itself carries, each by the job it
    // came from. The titles and `ownProfit` name the engagement, so a project overrides them
    // below.
    brokerageTitle: 'עבודות מעבר לריטיינר',
    brokerageJob: 'עבודה',
    brokerageLineProfit: 'רווח',
    costsTitle: 'עלויות הריטיינר',
    costSource: 'מקור',
    costKindTopic: 'עלות העבודה',
    costKindAbsorbed: 'ספגנו',
    ownProfit: 'רווח נקי מהריטיינר',
    brokerageProfit: 'רווח מהעבודות',
    combinedProfit: 'סה״כ רווח נקי',
    // Everything the client was billed for the engagement and its jobs, whatever it cost.
    billedTotal: 'סה״כ חויב הלקוח',
    showActive: 'פעילים',
    showClosed: 'סגורים',
    // Empty states: one short line each.
    emptyActive: 'אין ריטיינרים פעילים',
    emptyClosed: 'אין ריטיינרים סגורים',
    emptyTopics: 'אין עבודות',
    emptyDoneTopics: 'אין עבודות שהסתיימו',
  },
  // A ONE-OFF PROJECT (migration 067): the same engagement as a retainer — jobs beneath
  // it, their costs and charges, its own split — billed as ONE fixed amount for its whole
  // term rather than monthly. Only the words that name the engagement or its billing
  // differ; every other label on these screens is the retainer's own, on purpose.
  projects: {
    title: 'פרויקטים חד־פעמיים',
    newProject: 'פרויקט חדש',
    editProject: 'עריכת פרויקט',
    projectTitle: 'שם הפרויקט',
    kind: 'פרויקט חד־פעמי',
    price: 'מחיר הפרויקט',
    remaining: 'נותר לחייב',
    startDate: 'תחילת הפרויקט',
    endDate: 'סיום הפרויקט',
    billingMode: 'סוג ההתקשרות',
    modeMonthly: 'ריטיינר חודשי',
    modeFixed: 'פרויקט חד־פעמי',
    fullyBilled: 'חויב במלואו',
    ownIncome: 'מחויבת בנפרד מהפרויקט',
    brokerageTitle: 'עבודות מעבר למחיר הפרויקט',
    costsTitle: 'עלויות הפרויקט',
    ownProfit: 'רווח נקי מהפרויקט',
    deleteProject: 'מחיקת פרויקט',
    deleteScope: 'יימחקו הפרויקט וכל העבודות שתחתיו, על החשבוניות, ההוצאות, המשימות והקבצים שלהן.',
    deleteJobs: 'עבודות שתחת הפרויקט',
    openProject: 'פתיחת הפרויקט',
    emptyActive: 'אין פרויקטים פעילים',
    emptyClosed: 'אין פרויקטים סגורים',
    invoices: 'הכנסות הפרויקט',
    // A monthly retainer's extra charges — income beyond the monthly fee, on the retainer
    // itself. A one-off project takes none.
    extras: 'הכנסות נוספות',
    addExtra: 'הכנסה נוספת',
    extraTitle: 'הכנסה נוספת',
    extraDescription: 'על מה ההכנסה',
    extraConfirm: 'רישום ההכנסה',
    total: 'סה״כ',
    noExtras: 'אין הכנסות נוספות',
  },
  // The signed contract behind a retainer. Uploaded from the retainer screen or from
  // the client card, which gathers every agreement across all the client's retainers.
  agreements: {
    // הסכם של עבודה — בדרך כלל מול ספק (הצלם), לא מול הלקוח. הכותרת נפרדת כדי ששני
    // סוגי ההסכמים לא ייקראו כאותו דבר.
    jobPanelTitle: 'הסכמים חתומים של העבודה',
    // The heading is short because the engagement is always already on screen — this
    // list only ever appears inside the retainer it belongs to.
    panelTitle: 'ההסכם החתום',
    add: 'העלאת הסכם',
    edit: 'עריכת הסכם',
    name: 'שם ההסכם',
    namePlaceholder: 'למשל: הסכם ריטיינר 2026',
    // The same field on a job's upload form, where a retainer example would be the wrong
    // kind of contract entirely — a job's agreement is usually with a supplier.
    jobNamePlaceholder: 'למשל: הסכם צלם למלווה מלכה',
    signedDate: 'תאריך החתימה',
    notes: 'הערות',
    file: 'קובץ',
    uploading: 'מעלה...',
    uploadedBy: 'הועלה על ידי',
    // The archive is a separate view: הסכם שחודש יוצא מהתצוגה אך נשמר במלואו.
    showArchive: 'הצגת הארכיון',
    showLive: 'חזרה להסכמים הפעילים',
    archiveTitle: 'ארכיון הסכמים',
    archivedBy: 'הועבר לארכיון על ידי',
    restore: 'שחזור מהארכיון',
    // The two-choice dialog. It always names the agreement, because one of these
    // options destroys what may be the only copy of a signed contract.
    remove: 'הסרה',
    removeTitle: 'הסרת הסכם',
    removeQuestion: 'מה לעשות עם ההסכם?',
    archiveAction: 'הוצאה מהתצוגה',
    archiveHint: 'ההסכם ייצא מהרשימה אך הקובץ יישמר במלואו, יישאר ניתן לצפייה ולהורדה בארכיון, וניתן יהיה לשחזר אותו בכל רגע. זו הבחירה הנכונה להסכם שחודש או הוחלף.',
    deleteAction: 'מחיקה לצמיתות',
    deleteHint: 'הרשומה והקובץ יימחקו מהאחסון לחלוטין ולא ניתן יהיה לשחזר אותם. מיועד לקובץ שהועלה בטעות.',
  },
  // The CONTRACT's state, not the work's — a finished topic under a live retainer is
  // normal, so the two status sets are separate.
  retainerStatus: {
    active: 'פעיל',
    completed: 'הסתיים',
    cancelled: 'בוטל',
  },
};

export default retainers;
